import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { TFMark } from './TFLogo';
import { PRIVATE_SECTIONS } from '../data/private_content';
import { Lock, ArrowRight, ArrowLeft, Sun, Moon } from '@phosphor-icons/react';
import { PV_THEME, usePvMode } from './private/PrivateLayout';

import ManifestoSection from './private/sections/Manifesto';
import EcosystemSection from './private/sections/Ecosystem';
import WhitepaperSection from './private/sections/Whitepaper';
import InvestorsSection from './private/sections/Investors';
import MarketIntelSection from './private/sections/MarketIntel';
import FundingSection from './private/sections/Funding';
import ArtistCVSection from './private/sections/ArtistCV';
import InsolvencySection from './private/sections/Insolvency';
import CalculatorSection from './private/sections/Calculator';
import AiChatPanel from './AiChatPanel';

const API = process.env.REACT_APP_BACKEND_URL;
const PRIVATE_API = `${API}/api/private`;

const SECTION_COMPONENTS = {
  manifesto: ManifestoSection,
  ecosystem: EcosystemSection,
  whitepaper: WhitepaperSection,
  investors: InvestorsSection,
  funding: FundingSection,
  intel: MarketIntelSection,
  calculator: CalculatorSection,
  'artist-cv': ArtistCVSection,
  insolvency: InsolvencySection,
};

const T = PV_THEME;

// ── Password Gate ────────────────────────────────────────────
function PasswordGate({ onUnlock }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [_mode, _toggle] = usePvMode(); // applies CSS vars to root

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${PRIVATE_API}/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      if (res.ok) {
        onUnlock();
        return;
      }
      if (res.status === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
      } else if (res.status === 503) {
        setError('Private View is not configured on this deployment. Contact investors@trustframe.legal.');
      } else {
        setError('Access denied. Re-enter your code or request access at investors@trustframe.legal.');
      }
      setPw('');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: T.bg, color: T.text }} data-testid="private-gate">
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{ backgroundImage: `radial-gradient(circle, ${T.gridDot} 1px, transparent 1px)`, backgroundSize: '28px 28px' }}
      />
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b" style={{ background: T.overlayBg, borderColor: T.borderFaint }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 sm:gap-5" data-testid="gate-public-home">
            <TFMark color={T.text} size={22} />
            <span className="hidden xs:inline font-machina font-bold text-[10px] uppercase whitespace-nowrap" style={{ letterSpacing: '0.9em', paddingLeft: '0.9em', color: T.text }}>
              TRUSTFRAME
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={_toggle}
              data-testid="gate-theme-toggle"
              aria-label={_mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="inline-flex items-center justify-center min-h-[36px] min-w-[36px] px-2 border transition-colors hover:opacity-100 opacity-80"
              style={{ borderColor: T.border, color: T.text }}
            >
              {_mode === 'dark' ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
            </button>
            <Link to="/" className="inline-flex items-center gap-2 font-mono text-[9px] tracking-[0.25em] uppercase opacity-70 hover:opacity-100 transition-opacity" style={{ color: T.text }}>
              <ArrowLeft size={12} weight="bold" /> <span className="hidden sm:inline">Public</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-md mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-40 pb-16">
        <p className="font-mono text-[10px] sm:text-xs tracking-[0.4em] uppercase mb-4" style={{ color: T.accent }} data-testid="gate-eyebrow">
          [ .ACCESS.RESTRICTED. ]
        </p>
        <h1 className="font-machina font-bold text-3xl sm:text-5xl uppercase mb-4 leading-[0.95]">Private View</h1>
        <p className="font-mono text-sm leading-relaxed mb-10" style={{ color: T.textMuted }}>
          Manifesto · Ecosystem · Whitepaper · Investors · Calculator · Market Intel.
          <br />
          Enter the access code to continue.
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="gate-form">
          <div>
            <label className="font-mono text-[10px] tracking-[0.25em] uppercase block mb-2" style={{ color: T.textMuted }}>
              Access code
            </label>
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
              data-testid="gate-password-input"
              className="w-full min-h-[48px] px-4 py-3 bg-transparent border outline-none font-mono text-sm tracking-[0.15em] transition-colors"
              style={{ borderColor: T.border, color: T.text }}
              placeholder="••••••••••••"
            />
          </div>

          {error && (
            <p data-testid="gate-error" className="font-mono text-xs" style={{ color: T.accent }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || !pw}
            data-testid="gate-submit"
            className="w-full min-h-[48px] px-6 py-3 font-machina font-bold text-xs tracking-[0.3em] uppercase transition-all hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-3"
            style={{ background: T.accent, color: T.accentInk }}
          >
            {submitting ? 'Verifying…' : 'Enter'}
            {!submitting && <ArrowRight size={14} weight="bold" />}
          </button>

          <p className="font-mono text-[10px] tracking-[0.2em] uppercase pt-4 break-words" style={{ color: T.textFaint }}>
            {'// '}Request access:{' '}
            <a href="mailto:investors@trustframe.legal" className="underline" style={{ color: T.accent }}>
              investors@trustframe.legal
            </a>
          </p>
        </form>
      </main>
    </div>
  );
}

// ── Card-grid index ─────────────────────────────────────────
function PrivateIndex() {
  const [mode, toggleMode] = usePvMode();
  const isDark = mode === 'dark';

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: T.bg, color: T.text }} data-testid="private-index">
      {/* Background grid */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{ backgroundImage: `radial-gradient(circle, ${T.gridDot} 1px, transparent 1px)`, backgroundSize: '28px 28px' }}
      />
      <div
        className="pointer-events-none fixed inset-x-0 h-0.5 z-[60]"
        style={{ background: T.scanline, animation: 'pvScan 9s linear infinite' }}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b" style={{ background: T.overlayBg, borderColor: T.borderFaint }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
          <Link to="/private" className="flex items-center gap-3 sm:gap-5" data-testid="index-home-link">
            <TFMark color={T.text} size={22} />
            <span className="hidden xs:inline font-machina font-bold text-[10px] sm:text-[11px] uppercase whitespace-nowrap" style={{ letterSpacing: '0.9em', paddingLeft: '0.9em' }}>
              TRUSTFRAME
            </span>
            <span className="hidden md:inline font-mono text-[9px] tracking-[0.25em] uppercase pl-4 border-l" style={{ color: T.textFaint, borderColor: T.border }}>
              Private View · Index · MMXXVI
            </span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={toggleMode}
              data-testid="index-theme-toggle"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className="inline-flex items-center justify-center min-h-[36px] min-w-[36px] px-2 border transition-colors hover:opacity-100 opacity-80"
              style={{ borderColor: T.border, color: T.text }}
            >
              {isDark ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
            </button>
            <span className="inline-flex items-center gap-2 font-mono text-[9px] tracking-[0.25em] uppercase" style={{ color: T.accent }}>
              <Lock size={11} weight="fill" /> <span className="hidden sm:inline">UNLOCKED</span>
            </span>
            <Link to="/" data-testid="index-public-link" className="inline-flex items-center gap-2 font-mono text-[9px] tracking-[0.25em] uppercase opacity-70 hover:opacity-100 transition-opacity min-h-[36px] px-2" style={{ color: T.text }}>
              <ArrowLeft size={12} weight="bold" /> <span className="hidden sm:inline">Public</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-12">
        <p className="font-mono text-[10px] sm:text-xs tracking-[0.4em] uppercase mb-4" style={{ color: T.accent }}>
          [ .PRIVATE.VIEW.MMXXVI. ]
        </p>
        <h1 className="font-machina font-bold uppercase mb-6" style={{ fontSize: 'clamp(36px,7vw,72px)', lineHeight: 0.95, color: T.text }}>
          The data room.
        </h1>
        <p className="font-mono text-sm sm:text-base leading-relaxed max-w-3xl" style={{ color: T.textMuted }}>
          Manifesto, ecosystem map, whitepaper, investor deck, funding strategy, market intel, settlement calculator, and reference legal briefings — packaged for operators, capital allocators, and strategic partners.
        </p>
      </section>

      {/* Grid */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {PRIVATE_SECTIONS.map((s, i) => (
            <Link
              key={s.slug}
              to={`/private/${s.slug}`}
              data-testid={`card-${s.slug}`}
              className="group relative block rounded-[6px] p-6 sm:p-7 transition-all duration-300 hover:-translate-y-0.5"
              style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                animation: `pvFadeUp 0.5s ease both ${i * 0.05}s`,
              }}
            >
              {/* Top accent line on hover */}
              <div
                className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: s.accent, boxShadow: `0 0 12px ${s.accent}` }}
              />
              <div className="flex items-start justify-between gap-3 mb-5">
                <div className="font-mono text-[9px] tracking-[0.3em] uppercase" style={{ color: T.textFaint }}>
                  SEC.{s.num}
                </div>
                <span
                  className="font-mono text-[8px] tracking-[0.18em] uppercase px-2 py-1"
                  style={{
                    color: s.accent,
                    border: `1px solid ${s.accent}33`,
                    background: `${s.accent}0d`,
                  }}
                >
                  {s.tag}
                </span>
              </div>

              <h2
                className="font-machina font-bold uppercase whitespace-pre-line mb-3 leading-[1.05]"
                style={{ fontSize: 'clamp(20px,2.4vw,26px)', color: T.text }}
              >
                {s.title}
              </h2>

              <p className="font-sans leading-[1.7] mb-6" style={{ fontSize: 12.5, color: T.textMuted }}>
                {s.blurb}
              </p>

              <div className="flex items-center justify-between pt-4" style={{ borderTop: `1px solid ${T.borderFaint}` }}>
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase" style={{ color: s.accent }}>
                  {s.label}
                </span>
                <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-1" style={{ color: s.accent }} />
              </div>
            </Link>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-16 pt-10 flex flex-col sm:flex-row justify-between gap-4" style={{ borderTop: `1px solid ${T.border}` }}>
          <div className="flex items-center gap-3">
            <TFMark color={T.textMuted} size={18} />
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase" style={{ color: T.textMuted }}>
              TrustFrame OS · 2026
            </span>
          </div>
          <a
            href="mailto:investors@trustframe.legal"
            data-testid="index-contact-link"
            className="font-mono text-[10px] tracking-[0.25em] uppercase transition-opacity hover:opacity-100 opacity-70"
            style={{ color: T.accent }}
          >
            investors@trustframe.legal →
          </a>
        </div>
      </section>

      <style>{`
        @keyframes pvScan {
          0% { transform: translateY(0); }
          100% { transform: translateY(100vh); }
        }
        @keyframes pvFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ── Section dispatcher ──────────────────────────────────────
function SectionDispatcher() {
  const { section } = useParams();
  const navigate = useNavigate();
  const Comp = section ? SECTION_COMPONENTS[section] : null;

  useEffect(() => {
    if (section && !Comp) navigate('/private', { replace: true });
  }, [section, Comp, navigate]);

  if (!Comp) return null;
  return <Comp />;
}

// ── Main router-aware component ─────────────────────────────
export default function PrivateView() {
  const { section } = useParams();
  // null = checking, true = unlocked, false = locked
  const [authState, setAuthState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${PRIVATE_API}/check`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { authorized: false }))
      .then((j) => {
        if (!cancelled) setAuthState(Boolean(j.authorized));
      })
      .catch(() => {
        if (!cancelled) setAuthState(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (authState === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: T.bg, color: T.textMuted }}
        data-testid="private-loading"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-60">
          {'// '}Authenticating…
        </span>
      </div>
    );
  }

  if (!authState) return <PasswordGate onUnlock={() => setAuthState(true)} />;
  return (
    <>
      {section ? <SectionDispatcher /> : <PrivateIndex />}
      <AiChatPanel
        surface="private"
        storageKey="tf_pv_ai_session"
        title="TrustFrame Concierge"
        theme={{
          bg: T.bg,
          surface: T.surface,
          text: T.text,
          textMuted: T.textMuted,
          border: T.border,
          accent: T.accent,
          accentInk: '#0a0a0a',
        }}
        suggestions={[
          'Summarise the whitepaper in three bullets.',
          'What does §3 of the whitepaper say about escrow?',
          'How does the 50/50 artist-gallery split work in the calculator?',
          'Why is the gate insured before it is lent against?',
        ]}
      />
    </>
  );
}
